import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { AccountBookmarkService } from '@app/core/services/lingo2-account/account-bookmark';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { getMe } from '@store/reducers/profile.reducer';
import {
  Collection,
  Content,
  CSchool,
  EntityTypeEnum,
  FeatureEnum,
  IStatusResponse,
  Meeting,
  User,
  UserService,
  UserStatusEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, takeUntil } from 'rxjs';
import {
  AuthService,
  CollectionsService,
  ContentService,
  FeaturesService,
  MeetingsService,
  PlatformService,
  UserServicesService,
} from '../../services';

export type BookmarkContentType = 'user' | 'content' | 'meeting' | 'service' | 'collection' | 'school';

@Component({
  selector: 'app-bookmark',
  standalone: true,
  imports: [CommonModule, TranslateModule, IconComponent],
  templateUrl: './bookmark.component.html',
  styleUrls: ['./bookmark.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookmarkComponent extends ChangableComponent implements OnInit {
  readonly accountBookmarkService = inject(AccountBookmarkService);

  @Input() set item(data: Partial<User | Content | Meeting | UserService | Collection | CSchool>) {
    this._item = data;
    this.isFavorite = this._item?.visit_info?.is_favorite;
  }
  public get item() {
    return this._item;
  }

  @Input() type: BookmarkContentType = 'user';
  @Input() notooltip = false;
  @Output() favoriteChanged = new EventEmitter<boolean>();

  private _item: Partial<User | Content | Meeting | UserService | Collection | CSchool>;
  public isFavorite: boolean;
  public isGuest: boolean;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private store: Store,
    private authService: AuthService,
    private meetingsService: MeetingsService,
    private userServicesService: UserServicesService,
    private collectionService: CollectionsService,
    private contentService: ContentService,
    private features: FeaturesService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
    public deviceService: DeviceDetectorService,
  ) {
    super(cdr, platform);

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => (this.isGuest = me && me.status === UserStatusEnum.guest),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  ngOnInit(): void {}

  public onToggleFavorite() {
    if (this.isGuest) {
      this.authService.showAuthModal();
      return;
    }
    let action: Observable<IStatusResponse | User | Content | Meeting | UserService | Collection | CSchool | boolean>;

    switch (this.type) {
      case 'content':
        action = this.isFavorite
          ? this.contentService.removeBookmark(this.item.id)
          : this.contentService.addBookmark(this.item.id);
        break;
      case 'meeting':
        action = this.isFavorite
          ? this.meetingsService.removeBookmark(this.item.id)
          : this.meetingsService.addBookmark(this.item.id);
        break;
      case 'user':
        action = this.isFavorite
          ? this.accountBookmarkService.removeBookmark(this.item.id, EntityTypeEnum.account)
          : this.accountBookmarkService.addBookmark(this.item.id, EntityTypeEnum.account);
        break;
      case 'service':
        action = this.isFavorite
          ? this.userServicesService.removeBookmark(this.item.id)
          : this.userServicesService.addBookmark(this.item.id);
        break;
      case 'collection':
        action = this.isFavorite
          ? this.collectionService.removeBookmark(this.item.id)
          : this.collectionService.addBookmark(this.item.id);
        break;
      case 'school':
        action = this.isFavorite
          ? this.accountBookmarkService.removeBookmark(this.item.id, EntityTypeEnum.school)
          : this.accountBookmarkService.addBookmark(this.item.id, EntityTypeEnum.school);
        break;
    }

    action.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (value) => {
        this.isFavorite = !this.isFavorite;
        this.favoriteChanged.emit(this.isFavorite);
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        this.isFavorite = false;
        this.detectChanges();
      },
    });
  }

  public get isAvailableStatistics() {
    return this.features.isAvailable(FeatureEnum.statistics);
  }
}
